import { FF_C4C_ENABLE_CONNECT_VIDEO_CONSULT, FF_C4C_VIDEO_CONSULT } from '@/constants/featureFlags';
import { getAuthData } from '@/data/service/authService';
import { useFeatureFlags } from '@/hooks';
import { useTelecomWaitingRoom } from '@/hooks/telecom/useTelecomWaitingRoom';
import theme from '@/theme';
import { toast } from '@montugroup/design-system';
import type { TelecomWaitingRoomEntry } from '@montugroup/pms-api-contracts';
import { TelecomWaitingRoomEntryStatus } from '@montugroup/pms-api-contracts';
import { Box, Link } from '@mui/material';
import { useEffect, useState } from 'react';

export const WAITING_ROOM_NOTIFICATION_SUFFIX = 'has joined their consultation and is now in the Waiting Room';

type WaitingRoomNotificationProps = {
  calendarUrl: string;
};

const WaitingRoomNotification: React.FC<WaitingRoomNotificationProps> = ({ calendarUrl }) => (
  <Box
    sx={{
      width: '100%',
      display: 'flex',
      justifyContent: 'flex-end'
    }}
  >
    <Link sx={{ color: theme.palette.info.main }} href={calendarUrl}>
      Go to Calendar
    </Link>
  </Box>
);

export const useWaitingRoomNotifications = () => {
  const { flags } = useFeatureFlags();

  const isVideoConsultEnabled = flags[FF_C4C_VIDEO_CONSULT];
  const ffEnableConnectVideoConsult = flags[FF_C4C_ENABLE_CONNECT_VIDEO_CONSULT];

  const { isDoc, isNurse } = getAuthData();

  const enabled = isVideoConsultEnabled && ffEnableConnectVideoConsult && (isDoc || isNurse);

  const calendarUrl = isDoc ? '/calendar' : '/nurse-calendar';

  const { waitingRoom } = useTelecomWaitingRoom({ enabled });
  const [lastWaitingRoomEntries, setLastWaitingRoomEntries] = useState<TelecomWaitingRoomEntry[]>([]);

  useEffect(() => {
    if (!waitingRoom.length) {
      return;
    }
    const topThreeNewEntries = waitingRoom
      .filter((entry) => entry.status === TelecomWaitingRoomEntryStatus.Waiting)
      .filter((freshEntry) =>
        lastWaitingRoomEntries.every((staleEntry) => staleEntry.patientId !== freshEntry.patientId)
      )
      // order by queuedAt to show earliest at the top
      .toSorted((a, b) => a.queuedAt.localeCompare(b.queuedAt));

    //can pass { autoClose : number } as a second parameter to toast.info to control lifespan of toast. Default 5000ms
    topThreeNewEntries.forEach((entry) =>
      toast.info({
        title: `${entry.patientFirstName} ${entry.patientLastName} ${WAITING_ROOM_NOTIFICATION_SUFFIX}`,
        message: <WaitingRoomNotification calendarUrl={calendarUrl} />
      })
    );

    // don't clear the lastWaitingRoomEntries if there are no new entries
    if (topThreeNewEntries.length) {
      setLastWaitingRoomEntries(topThreeNewEntries);
    }

    // adding lastWaitingRoomEntries will cause a memory leak
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingRoom]);
};
